import {getMixPanelId} from "./MixPanelUtils";
import axios, {AxiosError, AxiosProgressEvent, AxiosResponse} from "axios";
import {AuthSession, fetchAuthSession} from "aws-amplify/auth";
import {isHumanSite} from "./siteUtils";

declare module "axios" {
  // the type parameter must match axios's own declaration for the augmentation to merge
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface AxiosRequestConfig<D = any> {
    authSession?: AuthSession | string;
  }
}

export type RequestOptions = {
  signal?: AbortSignal,
}

const BASE_PATH = (isHumanSite() ?
                   process.env["REACT_APP_HUMAN_API_BASE_PATH"] : process.env['REACT_APP_ARTISTREE_API_BASE_PATH']) + "/"

export const REQUEST_TIMEOUT_MS = 30_000;

export const apiClient = axios.create({
  baseURL: BASE_PATH,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {"Content-Type": "application/json"},
  transitional: {clarifyTimeoutError: true},
  // Resolved per request so an adapter installed on the default instance afterwards (axios-mock-adapter in tests) still applies
  adapter: (config) => axios.getAdapter(axios.defaults.adapter)(config),
});

// Human always passes its session explicitly (its order Start/Commit calls are intentionally anonymous),
// so the ambient session lookup is Artistree-only.
function resolveSession(authSession?: AuthSession | string): Promise<AuthSession | string | undefined> {
  if (authSession !== undefined || isHumanSite()) {
    return Promise.resolve(authSession);
  }
  return fetchAuthSession().catch(() => undefined);
}

apiClient.interceptors.request.use(async (config) => {
  config.headers.set("Client-Id", getMixPanelId());

  const session = await resolveSession(config.authSession);
  if (typeof session === "string") {
    config.headers.set("Authorization", session);
  } else if (session && session.tokens) {
    if (session.tokens.idToken) {
      config.headers.set("Authorization", session.tokens.idToken.toString());
    }
    if (session.userSub) {
      config.headers.set("Auth-Id", session.userSub);
    }
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      return Promise.reject(error);
    }
    const message = error instanceof Error ? error.message : "Network error";
    return Promise.reject(new AxiosError(message, AxiosError.ERR_NETWORK));
  },
);

export function isNetworkOrTimeoutError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response &&
    (error.code === AxiosError.ERR_NETWORK || error.code === AxiosError.ETIMEDOUT || error.code === AxiosError.ECONNABORTED);
}

export function isCanceledError(error: unknown): boolean {
  return axios.isCancel(error);
}

export function fetchGetResponse<T extends any>(path: string,
                                                authSession?: AuthSession | string,
                                                passed_headers?: Record<string, string>,
                                                options?: RequestOptions): Promise<AxiosResponse<T>> {
  return apiClient.get<T>(path, {
    headers: passed_headers,
    authSession,
    signal: options?.signal,
  });
}

export function fetchGetBody<T extends any>(path: string,
                                            authSession?: AuthSession | string,
                                            passed_headers?: Record<string, string>,
                                            options?: RequestOptions): Promise<T> {
  return fetchGetResponse<T>(path, authSession, passed_headers, options)
    .then((res) => res.data)
}

export function fetchPostResponse<T extends any>(path: string, body: any, authSession?: AuthSession | string, options?: RequestOptions): Promise<AxiosResponse<T>> {
  return apiClient.post<T>(path, JSON.stringify(body), {
    authSession,
    signal: options?.signal,
  });
}

export function fetchPostBody<T extends any>(path: string, body: any, authSession?: AuthSession | string, options?: RequestOptions): Promise<T> {
  return fetchPostResponse<T>(path, body, authSession, options)
    .then((res) => res.data)
}

export function fetchDeleteResponse<T extends any>(path: string, authSession?: AuthSession | string, options?: RequestOptions): Promise<AxiosResponse<T>> {
  return apiClient.delete<T>(path, {
    authSession,
    signal: options?.signal,
  });
}

export function fetchDeleteBody<T extends any>(path: string, authSession?: AuthSession | string, options?: RequestOptions): Promise<T> {
  return fetchDeleteResponse<T>(path, authSession, options)
    .then((res) => res.data)
}

export function fetchPutResponse<T extends any>(path: string, body: any, authSession?: AuthSession | string, options?: RequestOptions): Promise<AxiosResponse<T>> {
  return apiClient.put<T>(path, JSON.stringify(body), {
    authSession,
    signal: options?.signal,
  })
}

export function fetchPutBody<T extends any>(path: string, body: any, authSession?: AuthSession | string, options?: RequestOptions): Promise<T> {
  return fetchPutResponse<T>(path, body, authSession, options)
    .then((res) => res.data)
}

// Direct-to-S3 presigned uploads: no base URL, no auth headers, no Client-Id, no timeout.
export function fetchPutUpload(url: string, contentType: string, body: any, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void): Promise<any> {
  return axios.put(url, body, {
    headers: {
      "Content-Type": contentType,
    },
    onUploadProgress: onUploadProgress,
  })
}

export function fetchPutImageUpload(url: string, imageFile: File, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void): Promise<any> {
  return fetchPutUpload(url, imageFile.type, imageFile, onUploadProgress);
}
